(() => {
  'use strict';
  if (window.UAVTheme) return;
  const observed = new Map();
  const onScreen = new Set();
  const $ = (root, selector) => root.querySelector(selector);
  const HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const setQuery = (key, value) => {
    const url = new URL(location.href);
    if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  };

  /* --- the mobile call bar --------------------------------------------------
     The bar's second control navigates away, which discards a part-filled
     form. So it leaves while the enquiry itself is on screen, and while the
     menu is open. The bar is never rendered on the page it links to. */

  function syncCallbar() {
    const bar = document.querySelector('[data-uav-callbar]');
    if (bar) bar.hidden = onScreen.size > 0 || document.body.classList.contains('uav-menu-open');
  }
  const enquiryWatch = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting ? onScreen.add(entry.target) : onScreen.delete(entry.target));
      syncCallbar();
    }, {rootMargin: '0px 0px -56px 0px'})
    : null;

  function closeMenu(header, focus = false) {
    const button = $(header, '[data-uav-menu-toggle]');
    const panel = $(header, '[data-uav-menu-panel]');
    if (!button || !panel) return;
    button.setAttribute('aria-expanded', 'false'); panel.hidden = true;
    panel.classList.remove('is-open'); header.classList.remove('is-menu-open');
    document.body.classList.remove('uav-menu-open');
    $(button, '[data-uav-toggle-label]').textContent = button.dataset.menuLabel;
    syncCallbar();
    if (focus) button.focus();
  }
  function headerState() {
    const header = document.querySelector('[data-uav-header]');
    if (!header) return;
    const hero = document.querySelector('.uav-hero');
    const first = document.querySelector('#main > .shopify-section');
    header.classList.toggle('is-at-top', !!hero && !!first?.contains(hero) && scrollY < 40);
  }

  /* --- enquiry context ------------------------------------------------------
     A product or event page hands the form a reference. It is held in hidden
     inputs and never depends on a dropdown, so every product resolves rather
     than only those on the first page of a collection. A reference that cannot
     be confirmed is reported to the buyer; it is never dropped in silence. */

  function announce(form, text) {
    const status = $(form, '[data-uav-status]');
    if (status) status.textContent = text;
  }
  function showContext(box, state, text, removable) {
    box.dataset.state = state;
    $(box, '[data-uav-context-line]').textContent = text;
    $(box, '[data-uav-remove]').hidden = !removable;
  }
  function echoContext(form) {
    // The message keeps the reference even after the chip is removed, and it
    // never overwrites a word the buyer has already typed.
    const box = $(form, '[data-uav-context]');
    const message = $(form, 'textarea');
    if (!box || !message) return;
    const equipment = $(form, '[data-uav-equipment]');
    const event = $(form, '[data-uav-event]');
    const lines = [];
    if (equipment?.value) lines.push(`${box.dataset.labelEquipment} ${equipment.value}`);
    if (event?.value) lines.push(`${box.dataset.labelEvent}: ${event.value}`);
    if (!lines.length) return;
    if (message.value && message.value !== message.dataset.uavEcho) return;
    message.value = message.dataset.uavEcho = lines.join('\n') + '\n\n';
  }
  function unconfirmed(form, box, reference) {
    $(form, '[data-uav-equipment]').value = '';
    const text = `${box.dataset.textUnconfirmed} ${reference}`;
    showContext(box, 'unconfirmed', text, false);
    announce(form, text);
  }
  async function attachEquipment(form, reference) {
    const box = $(form, '[data-uav-context]');
    if (!box) return;
    box.dataset.handle = reference;
    $(form, '[data-uav-equipment-reference]').value = reference;
    if (!HANDLE.test(reference)) return unconfirmed(form, box, reference);
    let item;
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${base}products/${encodeURIComponent(reference)}.js`, {headers: {Accept: 'application/json'}});
      if (response.ok) item = await response.json();
    } catch (_) { /* Reported below. A lookup that fails is never swallowed. */ }
    if (box.dataset.handle !== reference) return;
    const title = typeof item?.title === 'string' ? item.title.trim() : '';
    if (!title || item.handle !== reference) return unconfirmed(form, box, reference);
    const vendor = typeof item.vendor === 'string' ? item.vendor.trim() : '';
    const name = vendor && !title.toLowerCase().startsWith(vendor.toLowerCase()) ? `${vendor} ${title}` : title;
    $(form, '[data-uav-equipment]').value = name;
    const text = `${box.dataset.labelEquipment} ${name}`;
    showContext(box, 'attached', text, true);
    announce(form, text);
    echoContext(form);
  }
  function attachEvent(form, id) {
    const options = $(form, 'template[data-uav-event-options]');
    const field = $(form, '[data-uav-event]');
    if (!options || !field || field.value.trim()) return;
    const option = [...options.content.querySelectorAll('option')].find(entry => entry.value && entry.value === id);
    if (!option) return;
    field.value = option.textContent.trim();
    echoContext(form);
  }

  function retainEnquiryFields(form) {
    const nativeForm = form.closest('form');
    if (!nativeForm) return;
    const key = `uav-enquiry:${location.pathname}:${nativeForm.id}`;
    const fields = () => [...form.querySelectorAll('input[name^="contact["], textarea[name^="contact["]')];
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      sessionStorage.removeItem(key);
      if (form.dataset.uavFormState === 'error' && saved && Date.now() - saved.at < 20 * 60 * 1000) {
        fields().forEach(field => {
          if (typeof saved.values?.[field.name] === 'string') field.value = saved.values[field.name];
        });
      }
    } catch (_) {}
    if (form.dataset.uavFormState === 'success') return;
    nativeForm.addEventListener('submit', () => {
      const phone = $(form, 'input[name="contact[phone]"]');
      if (phone?.value.trim() === '+65') phone.value = '';
      try {
        const values = Object.fromEntries(fields().map(field => [field.name, field.value]));
        sessionStorage.setItem(key, JSON.stringify({at: Date.now(), values}));
      } catch (_) {}
    });
  }

  function initialise(root = document) {
    root.querySelectorAll('[data-uav-demo-submit]').forEach(button => button.disabled = false);
    root.querySelectorAll('[data-uav-enquiry]').forEach(form => {
      if (form.dataset.uavReady) return;
      form.dataset.uavReady = 'true';
      retainEnquiryFields(form);
      enquiryWatch?.observe(form.closest('.uav-section') || form);
      $(form, '[data-uav-outcome]')?.focus();
      const params = new URLSearchParams(location.search);
      const event = params.getAll('event');
      const equipment = params.getAll('equipment');
      if (event.length === 1) attachEvent(form, event[0].trim());
      if (equipment.length === 1) attachEquipment(form, equipment[0].trim().slice(0, 200));
    });
    document.querySelectorAll('[data-uav-header]').forEach(header => {
      if (observed.has(header)) return;
      const inner = $(header, '.uav-header__inner');
      const update = () => document.documentElement.style.setProperty('--uav-header-offset', Math.ceil(inner.getBoundingClientRect().height) + 'px');
      const observer = new ResizeObserver(update); observer.observe(inner); observed.set(header, observer); update();
    });
    const work = document.querySelector('[data-uav-work]');
    document.querySelectorAll('[data-uav-work-link]').forEach(link => {
      if (link.getAttribute('href') === '#work' || link.dataset.workDefault) {
        link.dataset.workDefault = 'true'; link.hidden = !work;
        if (work) link.href = '#' + work.id;
      }
    });
    syncCallbar();
    headerState();
  }
  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-uav-menu-toggle]');
    if (toggle) {
      const header = toggle.closest('[data-uav-header]');
      if (toggle.getAttribute('aria-expanded') === 'true') return closeMenu(header, true);
      const panel = $(header, '[data-uav-menu-panel]');
      toggle.setAttribute('aria-expanded', 'true'); panel.hidden = false;
      panel.classList.add('is-open'); header.classList.add('is-menu-open'); document.body.classList.add('uav-menu-open');
      $(toggle, '[data-uav-toggle-label]').textContent = toggle.dataset.closeLabel;
      syncCallbar(); $(panel, 'a[href]')?.focus(); return;
    }
    const navLink = event.target.closest('[data-uav-menu-panel] a[href]');
    if (navLink) closeMenu(navLink.closest('[data-uav-header]'));
    const remove = event.target.closest('[data-uav-remove]');
    if (remove) {
      const form = remove.closest('[data-uav-enquiry]');
      const box = $(form, '[data-uav-context]');
      if (!box) return;
      delete box.dataset.handle;
      $(form, '[data-uav-equipment]').value = '';
      $(form, '[data-uav-equipment-reference]').value = '';
      setQuery('equipment', null);
      showContext(box, 'none', '', false);
      announce(form, box.dataset.textRemoved);
      $(form, 'textarea')?.focus();
      return;
    }
    const demo = event.target.closest('[data-uav-demo-submit]');
    if (demo) {
      const form = demo.closest('[data-uav-demo]');
      const invalid = [...form.querySelectorAll('input,textarea')].find(field => !field.checkValidity());
      if (invalid) { invalid.reportValidity(); invalid.focus(); return; }
      announce(form, form.dataset.demoFeedback);
      $(form, '[data-uav-status]')?.focus();
    }
  });
  document.addEventListener('keydown', event => {
    const header = document.querySelector('[data-uav-header].is-menu-open'); if (!header) return;
    if (event.key === 'Escape') { event.preventDefault(); return closeMenu(header, true); }
    if (event.key !== 'Tab') return;
    const links = [...header.querySelectorAll('[data-uav-menu-panel] a[href], [data-uav-menu-panel] button')].filter(el => el.checkVisibility());
    if (!links.length) return;
    const target = event.shiftKey && document.activeElement === links[0] ? links.at(-1) : !event.shiftKey && document.activeElement === links.at(-1) ? links[0] : null;
    if (target) { event.preventDefault(); target.focus(); }
  });
  addEventListener('scroll', headerState, {passive:true});
  matchMedia('(min-width:1024px)').addEventListener('change', event => { if (event.matches) document.querySelectorAll('[data-uav-header]').forEach(h => closeMenu(h)); });
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:reorder', headerState);
  document.addEventListener('shopify:section:unload', event => {
    observed.forEach((observer, header) => { if (event.target.contains(header)) { observer.disconnect(); closeMenu(header); observed.delete(header); } });
    onScreen.forEach(target => { if (event.target.contains(target)) { enquiryWatch?.unobserve(target); onScreen.delete(target); } });
    requestAnimationFrame(() => initialise());
  });
  window.UAVTheme = {initialise};
  initialise();
})();
